import React, { useState } from 'react';

const ElegantDarkFeaturesSection = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);

  const features = [
    {
      icon: "🧠",
      title: "Intelligent Pattern Recognition",
      points: [
        "Detects high-probability price patterns with noise-reduction filters and statistical scoring",
        "Prioritizes historically reliable formations (e.g., U-shaped reversals, multi-peak structures)",
        "Integrates volume dynamics and real-time pattern confidence scores"
      ]
    },
    {
      icon: "🧭",
      title: "Market Regime Awareness",
      points: [
        "Dynamically classifies markets into trending, ranging, volatile, and corrective phases",
        "Adjusts internal parameters (stop-loss, lot size, and risk thresholds) based on regime",
        "Supports both proactive and reactive regime transitions with stability filters"
      ]
    },
    {
      icon: "📈",
      title: "Multi-Timeframe Signal Alignment",
      points: [
        "Confirms signals using a three-tiered timeframe model for precision entries",
        "Employs weighted confidence systems for strategic (trend), tactical (signal), and execution (timing) layers"
      ]
    },
    {
      icon: "💡",
      title: "Indicator Fusion Engine",
      points: [
        "Integrates order flow, momentum, trend, and volatility tools into one decision layer",
        "Applies machine-learned weightings to indicators depending on market type",
        "Generates composite scores for high-quality trade confirmation"
      ]
    },
    {
      icon: "⚙️",
      title: "Adaptive Risk & Position Management",
      points: [
        "Adjusts lot size based on pattern reliability, volatility, and confidence scoring",
        "Implements multi-layer stop systems: pattern-based, volatility-based, and time-based",
        "Employs smart breakeven and partial profit systems to preserve gains"
      ]
    },
    {
      icon: "♻️",
      title: "Structured Cost Averaging",
      points: [
        "Features an intelligent averaging system with limiters for controlled exposure",
        "Calculates unified take-profit and trailing logic for position clusters"
      ]
    },
    {
      icon: "📰",
      title: "News Sensitivity Integration",
      points: [
        "Filters high-impact economic events automatically",
        "Includes crypto-specific news alerts and volatility detection",
        "Optional mode for news-driven momentum trading with adjusted risk rules"
      ]
    },
    {
      icon: "📢",
      title: "Real-Time Communication & Control",
      points: [
        "Sends pattern alerts, trade signals, and risk notifications via Telegram",
        "Enables remote monitoring, emergency actions, and performance queries",
        "Logs detailed trade data for performance analytics and compliance"
      ]
    },
    {
      icon: "🧪",
      title: "Rigorous Testing & Quality Control",
      points: [
        "Stress-tested across 5+ years of historical data with variable spreads",
        "Validates each component with Monte Carlo simulations and walk-forward testing",
        "Includes statistical reporting per pattern, timeframe, and regime"
      ]
    },
    {
      icon: "🤖",
      title: "Machine Learning Optimization",
      points: [
        "Learns from historical outcomes to adjust parameters in real time",
        "Integrates advanced order-flow analytics and sentiment data for edge detection",
        "Continuously benchmarks pattern success rates vs. statistical expectations"
      ]
    }
  ];

  // Toggle feature expansion for mobile view
  const toggleFeature = (index) => {
    setExpandedFeature(expandedFeature === index ? null : index);
  };

  return (
    <>
      {/* KEY FEATURES header - moved to top of page */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 pt-5 sm:pt-8 pb-2 relative border-b border-gray-700">
        <div className="absolute top-0 right-0 w-24 h-24 sm:w-36 sm:h-36 bg-indigo-900 rounded-full mix-blend-multiply opacity-20 filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-36 sm:h-36 bg-blue-900 rounded-full mix-blend-multiply opacity-20 filter blur-3xl"></div>
        
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center justify-center mb-3">
              <div className="h-0.5 w-6 sm:w-8 md:w-12 bg-indigo-400 mr-2 sm:mr-4"></div>
              <span className="py-1 px-3 rounded-full text-indigo-300 bg-indigo-900/60 font-semibold text-sm border border-indigo-700">KEY FEATURES</span>
              <div className="h-0.5 w-6 sm:w-8 md:w-12 bg-indigo-400 ml-2 sm:ml-4"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-6 text-white tracking-tight px-2">Advanced Trading Technology</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-2 mb-2">
              HON-SONAR™ combines sophisticated algorithms with professional trading strategies
            </p>
            
            {/* Bottom accent */}
            
          </div>
        </div>
      </div>
      
      {/* Features section */}
      <section id="features" className="py-10 sm:py-16 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-24 h-24 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-indigo-900 rounded-full mix-blend-multiply opacity-20 filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-blue-900 rounded-full mix-blend-multiply opacity-20 filter blur-3xl"></div>
        
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          {/* Desktop view (md and up) - 2 column grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-800 rounded-xl sm:rounded-2xl p-6 shadow-xl border border-gray-700 transition-all duration-300 hover:shadow-indigo-900/30 hover:shadow-2xl hover:-translate-y-1 relative overflow-hidden group"
              >
                {/* Feature number indicator */}
                <div className="absolute top-3 right-3 font-bold text-gray-700 text-3xl lg:text-4xl select-none opacity-20 transition-opacity group-hover:opacity-40">
                  {index + 1}
                </div>
                
                {/* Feature content */}
                <div className="flex space-x-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="relative flex items-center justify-center">
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-blue-900 rounded-full opacity-80 w-12 h-12 lg:w-14 lg:h-14"></div>
                      <div className="text-2xl lg:text-3xl relative z-10 w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center">
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-grow">
                    <h3 className="text-lg lg:text-xl font-bold mb-3 text-indigo-300 tracking-tight">{feature.title}</h3>
                    <ul className="space-y-2">
                      {feature.points.map((point, i) => (
                        <li key={i} className="flex items-start">
                          <div className="h-4 w-4 rounded-full bg-indigo-900/60 flex-shrink-0 flex items-center justify-center mt-1 mr-2 border border-indigo-700/50">
                            <div className="h-1.5 w-1.5 rounded-full bg-indigo-400"></div>
                          </div>
                          <span className="text-sm lg:text-base text-gray-300">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
              </div>
            ))}
          </div>
          
          {/* Mobile view (below md) - Enhanced accordion style */}
          <div className="md:hidden w-full">
            <div className="w-full space-y-2">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden w-full"
                >
                  {/* Feature header - clickable */}
                  <div 
                    className="p-4 flex items-center space-x-3 cursor-pointer w-full"
                    onClick={() => toggleFeature(index)}
                  >
                    {/* Icon */}
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-blue-900 rounded-full opacity-80 w-10 h-10"></div>
                      <div className="text-xl relative z-10 w-10 h-10 flex items-center justify-center">
                        {feature.icon}
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-base font-bold text-indigo-300 tracking-tight flex-grow pr-2">{feature.title}</h3>
                    
                    {/* Expand/collapse indicator */}
                    <div className={`transform transition-transform ${expandedFeature === index ? 'rotate-180' : ''} flex-shrink-0`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Feature content - expandable */}
                  <div className={`overflow-hidden transition-all duration-300 ${expandedFeature === index ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="p-4 pt-0 pl-16">
                      <ul className="space-y-3">
                        {feature.points.map((point, i) => (
                          <li key={i} className="flex items-start">
                            <div className="h-4 w-4 rounded-full bg-indigo-900/60 flex-shrink-0 flex items-center justify-center mt-1 mr-3 border border-indigo-700/50">
                              <div className="h-1.5 w-1.5 rounded-full bg-indigo-400"></div>
                            </div>
                            <span className="text-sm text-gray-300">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Bottom accent line - only visible when expanded */}
                  <div className={`h-0.5 w-full bg-gradient-to-r from-indigo-500 to-blue-500 transition-opacity duration-300 ${expandedFeature === index ? 'opacity-100' : 'opacity-0'}`}></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Bottom accent */}
          <div className="flex justify-center mt-10">
            <div className="h-1 w-16 sm:w-20 md:w-24 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full"></div>
          </div>
        </div>
      </section>
      
      {/* CSS for grid pattern */}
      <style jsx="true" global="true">{`
        .bg-grid-pattern {
          background-image: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 15px 15px;
        }
        
        @media (min-width: 640px) {
          .bg-grid-pattern {
            background-size: 20px 20px;
          }
        }
        
        @media (min-width: 768px) {
          .bg-grid-pattern {
            background-size: 30px 30px;
          }
        }
      `}</style>
    </>
  );
};

export default ElegantDarkFeaturesSection;